import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import CommentModal from "./modals/CommentModal";
import "react-datepicker/dist/react-datepicker.css";
import DropdownField from "../core/DropdownField";
import CheckboxesField from "../core/CheckboxesField";
// import ListLast30YearsField from "../core/ListLast30YearsField";
import AffirmationField from "../core/AffirmationField";
import RadioSelectionField from "../core/RadioSelectionField";
import TableElement from "./TableElement";
import GroupElement from "./GroupElement";
// import TextFieldCustom from "components/TextFieldCustom";
import DatePickerCustom from "components/DatePickerCustom";
import TelephoneField from "../core/TelephoneField";
import TextAreaField from "../core/TextAreaField";
import { Button, Typography } from "@mui/material";
import {
    BOTTOM_SPACING_DEFAULT,
    CONTENT_ALIGNMENT_DEFAULT,
    HEADING_WEIGHT_DEFAULT,
} from "modules/pvs_activities/constants";
import { useSelector } from "react-redux";
import DynamicCCTable from "./dynamic_cc/DynamicCCTable";
import Heading from "../core/Heading";
import CCSummaryTable from "./CCSummaryTable";
import { debounce, isEqual } from "lodash";
import dayjs from "dayjs";
import { useLazyGetFormDataVariablesQuery } from "modules/pvs_activities/redux/service/formData";
import { FormDataContext } from "../contexts/FormContext";
import { mdToHtml, processVariables } from "modules/pvs_activities/helper.general";
import Experts from "./Experts";
import CustomCKEditor from "../CustomCKEditor";
import AttachmentField from "./AttachmentField";
import ExpertAction from "./ExpertAction";
import ExpertsCCTable from "./ExpertsCCTable";
import PeerReviewerAction from "./PeerReviewerAction";
import { FaExternalLinkAlt } from "react-icons/fa";
import DFormButton from "./DFormButton";
import PeerReviewerSelection from "./PeerReviewerSelection";
import DateFinalisation from "../core/DateFinalisation";
import SurveyElements from "./SurveyElements";
import ActionTakenPerCC from "modules/survey_activities/components/ActionTakenPerCC";

const DataElement = ({
    allowComments = true,
    id = "",
    templateId = "",
    value = "",
    data_element_id = "",
    data_element_type = "",
    number = "",
    show_number = false,
    label = "",
    properties = {},
    list_appearance = "",
    onChange = () => { },
    // onElementAttributeChange = () => {},
    multiple_selection = false,
    has_comments = false,
    description = "",
    isReadOnly = false,
    comments_count = 0,
    list_id = 0,
    can_modify_add = false,
    has_na = false,
    min_value = "",
    surveyMissionId = "",
    ...otherProps
}) => {
    const props = {
        allowComments,
        id,
        templateId,
        data_element_id,
        data_element_type,
        number,
        show_number,
        label,
        properties,
        list_appearance,
        onChange,
        multiple_selection,
        has_comments,
        description,
        isReadOnly,
        comments_count,
        list_id,
        can_modify_add,
        has_na,
        ...otherProps
    };

    const dataRef = useRef(value);
    const lastSavedDataRef = useRef(value);
    const editorInstanceRef = useRef(null);
    const formDataContext = useContext(FormDataContext);
    const siteLanguageKey = useSelector((s) => s.general.siteLanguageSF);
    const [triggerVariables, variablesRes] = useLazyGetFormDataVariablesQuery();

    const [commentsCount, setCommentsCount] = useState(0);
    const [refresh, setRefresh] = useState(false);

    useEffect(() => {
        if (formDataContext.mode !== "preview" && ![undefined, null, ""].includes(formDataContext.missionId)) {
            triggerVariables(formDataContext.missionId, true);
        }
    }, [formDataContext.missionId]);

    useEffect(() => {
        setCommentsCount(comments_count);
    }, [comments_count]);

    useEffect(() => {
        if (value !== lastSavedDataRef.current) {
            dataRef.current = value;
            lastSavedDataRef.current = value;
            if (editorInstanceRef.current && data_element_type === "description") {
                const editor = editorInstanceRef.current;

                // Wait until editor is ready to interact with the model
                editor.setData(value)?.then(() => {
                    const selection = editor.model.document.selection;
                    const position = selection.getFirstPosition();

                    // Check that the position is still valid
                    const totalOffset = position.offset;
                    const nodeCount = editor.model.document.getRoot().getChildCount();

                    if (totalOffset < nodeCount) {
                        editor.model.change((writer) => {
                            writer.setSelection(position);
                        });
                    }
                });
            }
        }
    }, []);

    const getLabel = useMemo(() => {
        const tmp =
            siteLanguageKey === "en"
                ? label
                : siteLanguageKey === "fr"
                    ? properties.label_fr
                    : siteLanguageKey === "es"
                        ? properties.label_es
                        : "";
        return show_number ? `${number} ${tmp}` : tmp;
    }, [siteLanguageKey, label, properties.label_fr, properties.label_es, number, show_number]);

    const handleChange = (val) => {
        if (data_element_type === "date") {
            dataRef.current = val ? dayjs(val).format(properties?.date_field_view === "MY" ? "MMM-YYYY" : "DD-MMM-YYYY") : null;
        } else {
            dataRef.current = val;
        }
        setRefresh(r => !r);
        updateValueToParent();
    };
    const updateValueToParent = useCallback(
        debounce(() => {
            if (dataRef.current === lastSavedDataRef.current) return;
            onChange(id, dataRef.current ?? "");
            lastSavedDataRef.current = dataRef.current;
        }, 1000),
        [onChange, id]
    );
    const shouldShowElement = useMemo(() => {
        try {
            if (formDataContext.mode === "preview") return true;
            if (properties.is_dependent === true) {
                const val = processVariables(variablesRes, properties.dependent_variable, true);
                if (properties.dependent_operation === "eq") {
                    return val === properties.dependent_value;
                } else if (properties.dependent_operation === "lt") {
                    return val < properties.dependent_value;
                } else if (properties.dependent_operation === "lt_eq") {
                    return val <= properties.dependent_value;
                } else if (properties.dependent_operation === "gt") {
                    return val > properties.dependent_value;
                } else if (properties.dependent_operation === "gt_eq") {
                    return val >= properties.dependent_value;
                } else {
                    return true;
                }
            }
        } catch (e) {
            if (e.type === "error") {
                console.log(e);
            }
        }
        return true;
    }, [properties, variablesRes]);

    return shouldShowElement ? (
        <>
            <div className={properties?.bottom_spacing ?? BOTTOM_SPACING_DEFAULT} key={id}>
                {(parseInt(has_comments) === 1 && allowComments) || getLabel ? (
                    <div className={`d-flex ${data_element_type !== "heading" ? "pt-1" : ""}`}>
                        {getLabel &&
                            ![
                                "group",
                                "horizontal_line",
                                "date",
                                "text",
                                "email",
                                "number",
                                "percentage",
                                "year",
                                "read_only_variable",
                                "list",
                            ].includes(data_element_type) && (
                                <Heading
                                    size={properties?.heading_size ?? "h5"}
                                    align={properties?.heading_align ?? CONTENT_ALIGNMENT_DEFAULT}
                                    weight={properties?.heading_weight ?? HEADING_WEIGHT_DEFAULT}
                                    font={properties?.heading_font}
                                    italic={properties?.heading_is_italic ?? false}
                                    underline={properties?.heading_is_underline ?? false}
                                    label={mdToHtml(getLabel)}
                                    className={"my-auto"}
                                />
                            )}
                    </div>
                ) : (
                    ""
                )}
                {["read_only_variable"].includes(data_element_type) && (
                    <>
                        <Heading
                            size={properties?.heading_size}
                            align={properties?.heading_align ?? CONTENT_ALIGNMENT_DEFAULT}
                            weight={properties?.heading_weight ?? HEADING_WEIGHT_DEFAULT}
                            italic={properties?.heading_is_italic ?? false}
                            underline={properties?.heading_is_underline ?? false}
                            font={properties?.heading_font}
                            label={processVariables(variablesRes, mdToHtml(getLabel))}
                        />
                        <div
                            ref={(el) => {
                                if (el) {
                                    const links = el.querySelectorAll("a");
                                    links.forEach(link => link.setAttribute("target", "_blank"));
                                }
                            }}
                            dangerouslySetInnerHTML={{
                                __html:
                                    formDataContext.mode === "preview"
                                        ? properties.variable_to_show
                                        : processVariables(variablesRes, properties.variable_to_show),
                            }}
                        />
                    </>
                )}
                {["static_text"].includes(data_element_type) && (
                    <Typography
                        align={properties?.heading_align ?? CONTENT_ALIGNMENT_DEFAULT}
                        dangerouslySetInnerHTML={{
                            __html:
                                siteLanguageKey === "en"
                                    ? processVariables(variablesRes, mdToHtml(description))
                                    : siteLanguageKey === "fr"
                                        ? processVariables(variablesRes, mdToHtml(properties.content_fr))
                                        : siteLanguageKey === "es"
                                            ? processVariables(variablesRes, mdToHtml(properties.content_es))
                                            : "",
                        }}
                    />
                )}
                {data_element_type === "description" &&
                    (isReadOnly ? (
                        <div
                            dangerouslySetInnerHTML={{ __html: dataRef.current ? dataRef.current : "-- No Data --" }}
                        />
                    ) : (
                        <CustomCKEditor
                            key={id}
                            height="200"
                            value={dataRef.current}
                            onChange={(val) => handleChange(val)}
                            required={!!+(properties?.is_required ?? "0")}
                            onReady={(editor) => {
                                editorInstanceRef.current = editor;
                            }}
                        />
                    ))}
                {["text"].includes(data_element_type) &&
                    (isReadOnly ? (
                        <>
                            <Typography variant={"body1"} sx={{ fontWeight: 600 }}>
                                {getLabel}
                            </Typography>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: dataRef.current ? dataRef.current : "-- No Data --",
                                }}
                            />
                        </>
                    ) : (
                        <TextAreaField
                            key={id}
                            label={getLabel}
                            type={data_element_type}
                            value={dataRef.current}
                            onChange={(val) => handleChange(val)}
                            onBlur={() => { }}
                            error={false}
                            required={!!+(properties?.is_required ?? "0")}
                        />
                    ))}
                {data_element_type === "email" &&
                    (isReadOnly ? (
                        <>
                            <Typography variant={"body1"} sx={{ fontWeight: 600 }}>
                                {getLabel}
                            </Typography>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: dataRef.current ? dataRef.current : "-- No Data --",
                                }}
                            />
                        </>
                    ) : (
                        <TextAreaField
                            key={id}
                            label={getLabel}
                            type={data_element_type}
                            value={dataRef.current}
                            onChange={(val) => handleChange(val)}
                            onBlur={() => { }}
                            error={false}
                            required={!!+(properties?.is_required ?? "0")}
                        />
                    ))}
                {["number", "year", "percentage"].includes(data_element_type) &&
                    (isReadOnly ? (
                        <>
                            <Typography variant={"body1"} sx={{ fontWeight: 600 }}>
                                {getLabel}
                            </Typography>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: dataRef.current ? dataRef.current : "-- No Data --",
                                }}
                            />
                        </>
                    ) : (
                        <TextAreaField
                            key={id}
                            label={getLabel}
                            type="number"
                            value={dataRef.current}
                            onChange={(val) => handleChange(val)}
                            onBlur={() => { }}
                            error={false}
                            required={!!+(properties?.is_required ?? "0")}
                        />
                    ))}
                {data_element_type === "telephone" &&
                    (isReadOnly ? (
                        <>
                            <Typography variant={"body1"} sx={{ fontWeight: 600 }}>
                                {getLabel}
                            </Typography>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: dataRef.current ? dataRef.current : "-- No Data --",
                                }}
                            />
                        </>
                    ) : (
                        <TelephoneField
                            key={id}
                            label={getLabel}
                            value={dataRef.current}
                            onChange={(val) => handleChange(val)}
                            required={!!+(properties?.is_required ?? "0")}
                        />
                    ))}
                {data_element_type === "date" &&
                    (isReadOnly ? (
                        <>
                            <Typography variant={"body1"} sx={{ fontWeight: 600 }}>
                                {getLabel}
                            </Typography>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: dataRef.current
                                        ? dataRef.current.format(
                                            properties?.date_field_view === "MY" ? "MMM-YYYY" : "DD-MMM-YYYY"
                                        )
                                        : "-- No Data --",
                                }}
                            />
                        </>
                    ) : (
                        <DatePickerCustom
                            label={getLabel}
                            sx={{ width: "100%" }}
                            fullWidth
                            className="form-control"
                            onChange={(val) => handleChange(val)}
                            onError={() => (handleChange(null))}
                            value={dataRef.current && dayjs(dataRef.current).isValid() ? dayjs(dataRef.current) : null}
                            // textField={(params) => (
                            //     <TextFieldCustom {...params} variant="standard" placeholder="DD-MMM-YY" disabled />
                            // )}
                            placeholder={properties?.date_field_view === "MY" ? "MMM-YYYY" : "DD-MMM-YYYY"}
                            format={properties?.date_field_view === "MY" ? "MMM-YYYY" : "DD-MMM-YYYY"}
                            inputFormat={properties?.date_field_view === "MY" ? "MMM-YYYY" : "DD-MMM-YYYY"}
                            views={properties?.date_field_view === "MY" ? ["month", "year"] : ["day", "month", "year"]}
                            required={!!+(properties?.is_required ?? "0")}
                            minDate={
                                properties?.start_date === "today"
                                    ? dayjs().startOf('day')
                                    : properties?.start_date === "custom"
                                        ? dayjs(min_value).startOf('day')
                                        : properties?.start_date === "variable"
                                            ? (
                                                (() => {
                                                    const selectedDate = dayjs(processVariables(variablesRes, properties?.dependent_variable));
                                                    return selectedDate && selectedDate.isValid() && selectedDate.isAfter(dayjs().startOf('day'))
                                                        ? selectedDate
                                                        : dayjs().add(1, 'day').startOf('day');
                                                })()
                                            )
                                            : undefined
                            }
                        />
                    ))}
                {["attachment"].includes(data_element_type) && (
                    <AttachmentField
                        elementId={id}
                        categoryId={properties.category_id ?? ""}
                        subcategoryId={properties.subcategory_id ?? ""}
                        typeId={properties.type_id ?? ""}
                        isReadOnly={isReadOnly}
                    />
                )}
                {data_element_type === "list" &&
                    list_appearance === "dropdown" &&
                    (isReadOnly ? (
                        <>
                            <Typography variant={"body1"} sx={{ fontWeight: 600 }}>
                                {getLabel}
                            </Typography>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: dataRef.current ? dataRef.current?.content : "-- No Data --",
                                }}
                            />
                        </>
                    ) : (
                        <DropdownField
                            key={id}
                            label={getLabel}
                            canAdd={can_modify_add}
                            templateId={templateId}
                            listID={list_id}
                            multiple={multiple_selection}
                            value={dataRef.current}
                            onChange={(val) => handleChange(val)}
                            required={!!+(properties?.is_required ?? "0")}
                            processVariables={properties?.list_values_have_variables ?? false}
                            missionId={formDataContext.missionId}
                        />
                    ))}
                {data_element_type === "list" &&
                    list_appearance === "checkboxes" &&
                    (isReadOnly ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: (() => {
                                    if (Array.isArray(dataRef.current)) {
                                        return dataRef.current
                                            .map((v) => v?.content || String(v))
                                            .filter(Boolean)
                                            .join(", ") || "-- No Data --";
                                    }
                                    if (dataRef.current && typeof dataRef.current === "object") {
                                        return dataRef.current.content || "-- No Data --";
                                    }
                                    return dataRef.current || "-- No Data --";
                                })(),
                            }}
                        />
                    ) : (
                        <CheckboxesField
                            key={id}
                            label={getLabel}
                            canAdd={can_modify_add}
                            templateId={templateId}
                            listID={list_id}
                            value={Array.isArray(dataRef.current) ? dataRef.current : dataRef.current ? [dataRef.current] : []}
                            onChange={(val) => handleChange(val)}
                            required={!!+(properties?.is_required ?? "0")}
                            processVariables={properties?.list_values_have_variables ?? false}
                            missionId={formDataContext.missionId}
                            viewVertical={(properties?.list_alignment_mode ?? "h") === "v"}
                        />
                    ))}
                {data_element_type === "affirmation" &&
                    (isReadOnly ? (
                        <div
                            dangerouslySetInnerHTML={{ __html: dataRef.current ? dataRef.current : "-- No Data --" }}
                        />
                    ) : (
                        <AffirmationField
                            key={id}
                            label={getLabel}
                            isMinimal={false}
                            hasNA={false}
                            name={`affirmation_${id}`}
                            value={dataRef.current}
                            onChange={(e, val) => handleChange(val)}
                            reason={has_na === 1 ? "" : ""}
                            onReasonChange={(e, val) => { }}
                            onReasonClick={() => { }}
                            required={!!+(properties?.is_required ?? "0")}
                            viewVertical={(properties?.list_alignment_mode ?? "h") === "v"}
                        />
                    ))}
                {data_element_type === "radio_selection" &&
                    (isReadOnly ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: dataRef.current ? dataRef.current?.content : "-- No Data --",
                            }}
                        />
                    ) : (
                        <RadioSelectionField
                            key={id}
                            label={getLabel}
                            readonly={isReadOnly}
                            listID={list_id}
                            value={dataRef.current}
                            onChange={(val) => handleChange(val)}
                            hasNA={has_na}
                            reason={has_na === 1 ? "" : ""}
                            onReasonChange={(e, val) => { }}
                            onReasonBlur={() => { }}
                            required={!!+(properties?.is_required ?? "0")}
                            processVariables={properties?.list_values_have_variables ?? false}
                            missionId={formDataContext.missionId}
                            viewVertical={(properties?.list_alignment_mode ?? "h") === "v"}
                        />
                    ))}
                {data_element_type === "date_finalisation" &&
                    (isReadOnly ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: dataRef.current ? dataRef.current : "-- No Data --",
                            }}
                        />
                    ) : (
                        <DateFinalisation
                            key={id}
                            label={getLabel}
                            readonly={isReadOnly}
                            listID={list_id}
                            value={dataRef.current}
                            onChange={(val) => handleChange(val)}
                            hasNA={has_na}
                            reason={has_na === 1 ? "" : ""}
                            onReasonChange={(e, val) => { }}
                            onReasonBlur={() => { }}
                            required={!!+(properties?.is_required ?? "0")}
                            processVariables={properties?.list_values_have_variables ?? false}
                            missionId={formDataContext.missionId}
                            viewVertical={(properties?.list_alignment_mode ?? "h") === "v"}
                            plain={true}
                        />
                    ))}


                {props.data_element_type === "form_table" && (
                    <TableElement
                        templateId={templateId}
                        tableId={data_element_id}
                        element={props}
                        isReadOnly={isReadOnly}
                    />
                )}
                {data_element_type === "group" && (
                    <GroupElement templateId={templateId} id={id} element={props} isReadOnly={isReadOnly} />
                )}
                {data_element_type === "horizontal_line" && <hr />}

                {data_element_type === "dynamic_cc_table" && (
                    <DynamicCCTable id={id} element={props} isReadOnly={isReadOnly} />
                )}
                {data_element_type === "cc_summary_table" && (
                    <CCSummaryTable id={id} element={props} isReadOnly={isReadOnly} />
                )}
                {data_element_type === "experts" && <Experts id={id} element={props} isReadOnly={isReadOnly} />}

                {data_element_type === "survey_form" &&
                    <SurveyElements surveyMissionId={surveyMissionId} />}
                {data_element_type === "survey_action_taken" &&
                    <ActionTakenPerCC surveyMissionId={surveyMissionId} />}
                {data_element_type === "peer_reviewer_selection" && (
                    <PeerReviewerSelection id={id} element={props} isReadOnly={isReadOnly} />
                )}
                {data_element_type === "expert_action" && (
                    <ExpertAction
                        id={id}
                        element={props}
                        isReadOnly={isReadOnly}
                        value={dataRef.current}
                        onChange={(val) => handleChange(val)}
                    />
                )}
                {data_element_type === "peer_reviewer_action" && (
                    <PeerReviewerAction
                        id={id}
                        element={props}
                        isReadOnly={isReadOnly}
                        value={dataRef.current}
                        onChange={(val) => handleChange(val)}
                    />
                )}
                <span className="d-flex justify-content-end pt-3">
                    {parseInt(has_comments) === 1 && allowComments && (
                        <div className={`ms-auto my-auto ${commentsCount > 0 ? "pt-2" : ""} `}>
                            <CommentModal
                                elementId={id}
                                commentsCount={commentsCount}
                                setCommentsCount={setCommentsCount}
                            />
                        </div>
                    )}
                </span>
                {data_element_type === "experts_cc_table" && (
                    <ExpertsCCTable id={id} element={props} isReadOnly={isReadOnly} />
                )}
                {data_element_type === "dform_button" && (
                    <DFormButton id={id} element={props} isReadOnly={isReadOnly} />
                )}
            </div>
        </>
    ) : null;
};

export default memo(DataElement, isEqual);
